import React, { FunctionComponent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  SaveEmailSubmitButtonGroup,
  ScreenReaderUpdateText,
  SlideIn,
} from 'components';
import {
  InputFormatQuestion,
  InputQuestion,
  SegmentedControlQuestion,
  SelectQuestion,
} from 'scenes/common/components/Questions';
import { HomeFullPageProps, SpecifiedContentCover } from 'scenes/home/types';
import { getApiData } from 'scenes/common/helpers/apiData';
import { getSpecifiedContent } from 'scenes/home/selectors';
import { toCurrencyWithSign } from 'scenes/common/helpers/currency';
import { InsuredItem } from 'scenes/common/types';
import { productDisclosureStatement } from 'scenes/common/helpers/contacts';
import { filterOptions } from './antiquesCollectionsFilter';
import InformationTable from '../SpecifiedContentsAnswerStep/InformationTable';

const CATEGORY = 'category';
const DESCRIPTION = 'description';
const VALUE = 'itemValue';
const COVER = 'cover';

const MIN_VALUE = 1;
const MAX_VALUE = 10000;

const COVER_OPTIONS = [
  {
    value: SpecifiedContentCover.Home,
    label: SpecifiedContentCover.Home,
  },
  {
    value: SpecifiedContentCover.PortableContents,
    label: SpecifiedContentCover.PortableContents,
  },
];

const getHelpText = (cover: SpecifiedContentCover) => {
  let helpText;

  switch (cover) {
    case SpecifiedContentCover.Home:
      helpText = (
        <>
          Specifying items for cover inside the {COVER_OPTIONS[0].label} does
          not cost you anything extra as long as you&apos;ve already included
          them in your total contents sum insured.
          <br />
          For full details, see Specified contents - inside your home in the{' '}
          <a
            href={productDisclosureStatement.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {productDisclosureStatement.text}
          </a>
          .
        </>
      );
      break;
    case SpecifiedContentCover.PortableContents:
      helpText = (
        <>
          You can choose to cover your {COVER_OPTIONS[1].label} Australia-wide –
          that is, inside your home, in the open air at your site and outside
          your site. This will provide cover for theft, loss or damage. A
          separate {COVER_OPTIONS[1].label} cover excess applies.
          <br />
          For full details please read the{' '}
          <a
            href={productDisclosureStatement.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {productDisclosureStatement.text}
          </a>
          .
        </>
      );
      break;
    default:
      helpText = (
        <>
          Cover in the {InsuredItem.Home} for specified contents is at no extra
          cost. Items with a value greater than the limits shown below need to
          be listed in order to be covered for their full value. <br />
          Portable contents is an optional extra that covers you in the{' '}
          {InsuredItem.Home} and Australia-wide.
        </>
      );
      break;
  }
  return (
    <div style={{ marginBottom: '20px' }} data-testid="add-contents-help-text">
      {helpText}
    </div>
  );
};

const AddSpecifiedContentStep: FunctionComponent<HomeFullPageProps> = ({
  referenceData,
  funnelState,
  onAddSpecifiedContent,
  onUpdateSpecifiedContent,
  onClickSaveQuoteModal,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    getValues,
    watch,
  } = useForm({
    mode: 'onChange',
  });
  const { specifiedPortableCategory } = getApiData(referenceData);
  const item = getSpecifiedContent(funnelState);

  const onSubmit = useCallback(() => {
    const { category, description, itemValue, cover } = getValues();
    if (item.id >= 0) {
      onUpdateSpecifiedContent({
        ...item,
        category,
        description,
        itemValue,
        cover,
      });
    } else {
      onAddSpecifiedContent({
        ...item,
        category,
        description,
        itemValue,
        cover,
      });
    }
  }, [getValues, item, onAddSpecifiedContent, onUpdateSpecifiedContent]);

  const watchedCoverOption = watch(COVER, item.cover);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
      <ScreenReaderUpdateText />
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        stateKey={COVER}
        options={COVER_OPTIONS}
        defaultValue={item.cover}
        question="What type of specified contents cover would you like?"
        helpText={getHelpText(watchedCoverOption)}
        validationRules={{
          required: true,
          validate: {
            oneOf: () =>
              COVER_OPTIONS.some((option) => option.value === getValues(COVER)),
          },
        }}
      />

      {watchedCoverOption && (
        <SlideIn>
          <SelectQuestion
            question="Category"
            options={filterOptions(
              watchedCoverOption,
              specifiedPortableCategory
            )}
            control={control}
            isLoading={false}
            stateKey={CATEGORY}
            defaultValue={item.category}
          />
          <InputQuestion
            errors={errors}
            control={control}
            stateKey={DESCRIPTION}
            defaultValue={item.description}
            maxLength={100}
            validationRules={{ required: true }}
            question="Brand/Description"
            ariaRequired
          />
          <InputFormatQuestion
            size={4}
            prefix="$"
            decimalScale={0}
            errors={errors}
            ariaRequired
            placeholder=""
            control={control}
            stateKey={VALUE}
            question="Value"
            maxLength={7}
            defaultValue={item.itemValue}
            validationRules={{
              required: true,
              validate: {
                validCost: (value) =>
                  (value >= MIN_VALUE && value <= MAX_VALUE) ||
                  `Please select a value between ${toCurrencyWithSign(
                    MIN_VALUE
                  )} and ${toCurrencyWithSign(MAX_VALUE)}`,
              },
            }}
          />
        </SlideIn>
      )}
      <SaveEmailSubmitButtonGroup
        onClickSaveQuoteModal={onClickSaveQuoteModal}
        submitEnabled={hooksForm.isValid}
      />
      <InformationTable cover={watchedCoverOption} />
    </form>
  );
};

export default AddSpecifiedContentStep;
